import type {
  NamespacedWireCommit,
  NamespacedWireCommitWithoutPayload,
  NamespacedWireDispatch,
  NamespacedWiringData,
} from '../../wiring/namespaced-wiring.types'
import {
  namespacedWireCommit,
  namespacedWireCommitWithoutPayload,
  namespacedWireDispatch,
} from '../../wiring/namespaced-wires.factory'
import { createWiring } from '../../wiring/wiring.utils'

/**
 * `nextQueries` {@link XModuleName | XModule name}.
 *
 * @internal
 */
const moduleName = 'nextQueries'

/**
 * WireCommit for {@link NextQueriesXModule}.
 *
 * @internal
 */
const wireCommit: NamespacedWireCommit<typeof moduleName> = namespacedWireCommit(moduleName)

/**
 * WireCommitWithoutPayload for {@link NextQueriesXModule}.
 */
const wireCommitWithoutPayload: NamespacedWireCommitWithoutPayload<typeof moduleName> =
  namespacedWireCommitWithoutPayload(moduleName)

/**
 * WireDispatch for {@link NextQueriesXModule}.
 *
 * @internal
 */
const wireDispatch: NamespacedWireDispatch<typeof moduleName> = namespacedWireDispatch(moduleName)

/**
 * Sets the next queries state `query`.
 *
 * @public
 */
export const setNextQueriesQuery = wireCommit('setQuery')

/**
 * Sets the next queries state `relatedTags`.
 *
 * @public
 */
export const setNextQueriesRelatedTags = wireCommit('setRelatedTags')

/**
 * Sets the next queries state `query` with a selectedQueryPreview's query.
 *
 * @public
 */
export const setNextQueriesQueryFromPreview = wireCommit(
  'setParams',
  ({ eventPayload: { query } }) => query,
)

/**
 * Sets the next queries state `query` from url.
 *
 * @public
 */
const setUrlParams = wireDispatch('setUrlParams')

/**
 * Sets the next queries state `params`.
 *
 * @public
 */
export const setNextQueriesExtraParams = wireCommit('setParams')

/**
 * Sets the next queries state `params` with a selectedQueryPreview's extraParams.
 *
 * @public
 */
export const setNextQueriesExtraParamsFromPreview = wireCommit(
  'setParams',
  ({ eventPayload: { extraParams } }) => extraParams,
)

/**
 * Requests and stores the next queries.
 *
 * @public
 */
export const fetchAndSaveNextQueriesWire = wireDispatch('fetchAndSaveNextQueries')

/**
 * Sets the next queries state `query` with the last query in history queries.
 *
 * @public
 */
export const setQueryFromLastHistoryQueryWire = wireDispatch('setQueryFromLastHistoryQuery')

/**
 * Requests and store the next query preview results.
 *
 * @public
 */
export const fetchAndSaveNextQueryPreviewWire = wireDispatch(
  'fetchAndSaveNextQueryPreview',
  ({ eventPayload: query, metadata: { location } }: NamespacedWiringData<'nextQueries'>) => {
    return {
      query,
      location,
    }
  },
)
/**
 * Resets the next query preview results.
 *
 * @public
 */
export const resetResultsPreviewWire = wireCommitWithoutPayload('resetResultsPreview')

/**
 * Sets the next queries state `searchedQueries` with the list of history queries.
 *
 * @public
 */
export const setSearchedQueries = wireCommit('setSearchedQueries')

/**
 * Wiring configuration for the {@link NextQueriesXModule | next queries module}.
 *
 * @internal
 */
export const nextQueriesWiring = createWiring({
  ParamsLoadedFromUrl: {
    setUrlParams,
  },
  NextQueriesChanged: {
    resetResultsPreviewWire,
  },
  UserAcceptedAQuery: {
    setNextQueriesQuery,
  },
  SelectedRelatedTagsChanged: {
    setNextQueriesRelatedTags,
  },
  SessionHistoryQueriesChanged: {
    setSearchedQueries,
    // TODO setQueryFromLastHistoryQuery it has to be called only one time
    setQueryFromLastHistoryQueryWire,
  },
  NextQueriesRequestUpdated: {
    fetchAndSaveNextQueriesWire,
  },
  ExtraParamsChanged: {
    setNextQueriesExtraParams,
  },
  NextQueryPreviewMountedHook: {
    fetchAndSaveNextQueryPreviewWire,
  },
  UserAcceptedAQueryPreview: {
    setNextQueriesQueryFromPreview,
    setNextQueriesExtraParamsFromPreview,
  },
})
